/**
 * Meta Pixel adapter (`window.fbq`).
 *
 * Fires only the events the ad platform pays for, with the shared
 * `event_id` so Meta CAPI deduplicates against the same fire from
 * `spicychat-backend`. PLAN.md §6.4 dedup contract.
 */

import type { EventEnvelope } from "@tracking/events";
import type { PixelAdapter, PixelEventMap } from "@tracking/pixels/types";

type FbqFunction = (
  command: "track" | "trackCustom",
  eventName: string,
  payload: Record<string, unknown>,
  options: { eventID: string },
) => void;

type WindowWithFbq = Window & { fbq?: FbqFunction };

const META_EVENT_MAP: PixelEventMap = {
  funnel_landed: "PageView",
  funnel_qualified: "Lead",
  funnel_account_created: "CompleteRegistration",
  funnel_paid_conversion: "Purchase",
};

const standardEventNames = new Set([
  "PageView",
  "Lead",
  "CompleteRegistration",
  "Purchase",
  "Subscribe",
  "AddToCart",
  "InitiateCheckout",
  "AddPaymentInfo",
  "ViewContent",
  "Search",
  "Contact",
]);

const resolveFbq = (): FbqFunction | null => {
  if (typeof window === "undefined") return null;
  const candidate = (window as WindowWithFbq).fbq;
  return typeof candidate === "function" ? candidate : null;
};

export const fireMetaPixel = (envelope: EventEnvelope): void => {
  const fbq = resolveFbq();
  if (fbq === null) return;
  const platformName = META_EVENT_MAP[envelope.event.name];
  if (platformName === undefined) return;
  const command = standardEventNames.has(platformName) ? "track" : "trackCustom";
  fbq(command, platformName, envelope.event.payload, {
    eventID: envelope.event_id,
  });
};

export const metaPixelAdapter: PixelAdapter = {
  platform: "meta",
  fire: fireMetaPixel,
};

/** Exported for tests so the event mapping is a regression target. */
export const META_PIXEL_EVENT_MAP: PixelEventMap = META_EVENT_MAP;
